import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug } from '@fortawesome/pro-light-svg-icons/faPlug';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { TypedMemo } from '../../../../common/types';
import { useConnectedApps } from './useConnectedApps';
import { ConnectedAppsList } from './ConnectedAppsList';
import { aiAssistantsMessages as messages } from '../aiAssistantsMessages';
import { Panel, PanelBody, PanelHead } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import apiStyles from '../../apiAccess/apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings → AI assistants → Connected assistants (design: ai-assistants a1-a7): the AI clients the
 * user approved over MCP for the active workspace, each with a Revoke button. It moved here from
 * the API tab (design: connected-apps a1-a4).
 */
const ConnectedAppsSectionComponent: React.FC = () => {
  const intl = useIntl();
  const { apps, isLoading, error, refetch, revokeApp, revokingClientId, revokeError } = useConnectedApps();

  return (
    <Panel as="section">
      <PanelHead
        titleAs="h2"
        title={intl.formatMessage(messages.connectedTitle)}
        subtitle={intl.formatMessage(messages.connectedDescription)}
        right={!isLoading && error === null && intl.formatMessage(messages.connectedCount, { count: apps.length })}
      />

      {isLoading && (
        <PanelBody>
          <LoadingView />
        </PanelBody>
      )}
      {!isLoading && error !== null && (
        <PanelBody>
          <ErrorView error={error} retry={refetch} />
        </PanelBody>
      )}
      {!isLoading && error === null && apps.length === 0 && (
        <div className={apiStyles.tokens}>
          <div className={apiStyles.empty}>
            <div className={apiStyles.emptyIcon}>
              <FontAwesomeIcon icon={faPlug} />
            </div>
            <b className={apiStyles.emptyTitle}>{intl.formatMessage(messages.connectedEmpty)}</b>
            <p className={apiStyles.emptyBody}>{intl.formatMessage(messages.connectedEmptyBody)}</p>
          </div>
        </div>
      )}
      {!isLoading && error === null && apps.length > 0 && (
        <ConnectedAppsList apps={apps} onRevoke={revokeApp} revokingClientId={revokingClientId} />
      )}
      {revokeError !== null && (
        <PanelBody>
          <ErrorView error={revokeError} />
        </PanelBody>
      )}
    </Panel>
  );
};

export const ConnectedAppsSection = TypedMemo(ConnectedAppsSectionComponent);
